/**
 * SurveyHeader
 *
 * Top section for the public survey page with campaign image/video, title,
 * description, and a "Get Started" CTA. Records survey start event.
 */
import React, { useState, useEffect } from 'react';
import { ChevronRight, Clock, Video, CheckSquare, AlertTriangle } from 'lucide-react';
import { post } from '../../../api/core';

export function SurveyHeader({ 
  campaign, 
  theme, 
  themes, 
  goToNextStep,
  customTheme,
  getTextStyle,
  getSubtextStyle,
  campaignId
}) {
  const [imageError, setImageError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  
  // Track if survey is full
  const surveyFull = campaign?.surveyFull;
  
  // Reset error states when campaign changes
  useEffect(() => {
    if (campaign?.campaignImage) {
      setImageError(false);
    }
    
    if (campaign?.videoUrl) {
      setVideoLoading(true);
      setVideoError(false);
    }
    
    // Log when surveyFull changes
    if (campaign?.surveyFull) {
      console.log('SurveyHeader: Survey is full');
    }
  }, [campaign?.campaignImage, campaign?.videoUrl, campaign?.surveyFull]);

  // Helper function to get the appropriate theme class
  const getThemeClass = (standardClass, customClass) => {
    if (!theme) return standardClass;
    if (theme === 'custom') return customClass;
    // Add optional chaining and fallback
    return themes[theme]?.[standardClass.split(' ')[0]] || standardClass;
  };

  // Handle "Get Started" button click
  const handleClick = () => {
    // Call the survey/start API endpoint
    if (campaignId) {
      // Fire and forget - we don't need to wait for the response
      post('/survey/start', { campaignId: campaignId })
        .then(() => {
          console.log('Survey start recorded');
        })
        .catch(error => {
          // Just log the error but don't block the user flow
          console.error('Failed to record survey start:', error);
        });
    }
    
    // Call the parent component's handler to move to the next step
    goToNextStep();
  };

  return (
    <div className="text-center">
      {campaign.campaignImage && (
        <div className="flex justify-center mb-6 mt-8">
          <div className="w-3/5 sm:w-2/5 md:w-1/3 lg:w-1/4 max-w-[300px] relative min-h-[180px] flex items-center justify-center">
            {/* Image Error State */}
            {imageError && (
              <div className={`absolute inset-0 flex flex-col items-center justify-center rounded-md ${
                theme 
                  ? theme === 'custom' ? 'bg-white/5 border border-white/10' : `${themes[theme]?.border || 'border-white/20'} bg-black/10` 
                  : 'bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700'
              }`}>
                <AlertTriangle className={`h-8 w-8 mb-2 ${
                  theme 
                    ? theme === 'custom' ? 'text-white/60' : themes[theme]?.text || 'text-white'
                    : 'text-amber-500 dark:text-amber-400'
                }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}} />
                <p className={`text-xs ${
                  theme 
                    ? theme === 'custom' ? 'text-white/80' : themes[theme]?.text || 'text-white'
                    : 'text-gray-500 dark:text-gray-400'
                }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
                  Image failed to load
                </p>
              </div>
            )}
            
            {/* Actual Campaign Image */}
            {!imageError && (
              <img 
                src={campaign.campaignImage}
                alt={campaign.title || 'Campaign Image'}
                className="w-full h-auto object-contain max-h-[200px] rounded-md"
                onError={(e) => {
                  console.error('Error loading campaign image');
                  setImageError(true);
                }}
              />
            )}
          </div>
        </div>
      )}
      
      {campaign.videoUrl && (
        <div className="flex justify-center mb-8">
          <div className="w-4/5 sm:w-3/5 md:w-2/5 lg:w-1/3 relative min-h-[200px]">
            {/* Video Loading Skeleton */}
            {videoLoading && !videoError && (
              <div className={`absolute inset-0 flex flex-col items-center justify-center rounded-md ${
                theme 
                  ? theme === 'custom' ? 'bg-white/5' : themes[theme]?.border || 'border-white/20'
                  : 'bg-gray-100 dark:bg-gray-800'
              } animate-pulse overflow-hidden`}>
                <div className="flex-1 flex items-center justify-center">
                  <Video className={`h-12 w-12 ${
                    theme 
                      ? theme === 'custom' ? 'text-white/40' : themes[theme]?.text || 'text-white'
                      : 'text-gray-400 dark:text-gray-600'
                  }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}} />
                </div>
                
                {/* Status message at bottom */}
                <div className={`w-full py-2 mt-auto ${
                  theme 
                    ? theme === 'custom' ? 'bg-white/10' : themes[theme]?.border || 'border-white/20'
                    : 'bg-gray-200 dark:bg-gray-700'
                }`}>
                  <p className={`text-xs ${
                    theme 
                      ? theme === 'custom' ? 'text-white/60' : themes[theme]?.text || 'text-white'
                      : 'text-gray-500 dark:text-gray-400'
                  }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
                    Loading video content...
                  </p>
                </div>
              </div>
            )}
            
            {/* Video Error State */}
            {videoError && (
              <div className={`absolute inset-0 flex flex-col items-center justify-center rounded-md ${
                theme 
                  ? theme === 'custom' ? 'bg-white/5 border border-white/10' : `${themes[theme]?.border || 'border-white/20'} bg-black/10`
                  : 'bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700'
              }`}>
                <AlertTriangle className={`h-8 w-8 mb-2 ${
                  theme 
                    ? theme === 'custom' ? 'text-white/60' : themes[theme]?.text || 'text-white'
                    : 'text-amber-500 dark:text-amber-400'
                }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}} />
                <p className={`text-xs ${
                  theme 
                    ? theme === 'custom' ? 'text-white/80' : themes[theme]?.text || 'text-white'
                    : 'text-gray-500 dark:text-gray-400'
                }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
                  Video failed to load
                </p>
              </div>
            )}
            
            {/* Actual Video with simplified loading */}
            <video 
              src={campaign.videoUrl} 
              controls
              playsInline
              className={`w-full h-auto object-contain max-h-[250px] rounded-md ${
                videoLoading || videoError ? 'invisible' : 'visible'
              }`}
              onLoadedData={() => {
                console.log('Explainer video loaded successfully');
                setVideoLoading(false);
              }}
              onError={(e) => {
                console.error('Error loading explainer video');
                setVideoLoading(false);
                setVideoError(true);
              }}
            />
          </div>
        </div>
      )}
      
      <h1 className={`mt-4 text-3xl font-bold tracking-tight ${
        theme 
          ? theme === 'custom' ? 'text-white' : themes[theme]?.text || 'text-white'
          : 'text-gray-900 dark:text-white'
      } sm:text-4xl`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
        {campaign?.title || 'Campaign Title'}
      </h1>
      <p className={`mt-4 text-base ${
        theme 
          ? theme === 'custom' ? 'text-white/80' : themes[theme]?.subtext || 'text-white/80'
          : 'text-gray-600 dark:text-gray-400'
      }`} style={theme === 'custom' && getSubtextStyle ? getSubtextStyle() : {}}>
        {campaign?.description || 'Campaign Description'}
      </p>
      
      {/* Only show the Get Started button if the survey is not full */}
      {!surveyFull && (
        <button
          onClick={handleClick}
          className={`mt-8 inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium ${
            theme 
              ? theme === 'custom' 
                ? 'border-white/20 border-2 text-white hover:bg-white/10' 
                : `${themes[theme]?.border || 'border-white/20'} border-2 ${themes[theme]?.text || 'text-white'} hover:bg-white/10`
              : 'bg-primary-600 text-white hover:bg-primary-700'
          }`}
          style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}
        >
          Get Started
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
      
      {/* Process notes - only show if survey is not full */}
      {!surveyFull && (
        <div className={`mt-10 mx-auto max-w-3xl ${
          theme 
            ? theme === 'custom' ? 'border-t border-white/10 pt-8' : `${themes[theme]?.border || 'border-white/20'} pt-8`
            : 'border-t border-gray-200 dark:border-gray-800 pt-8'
        }`}>
          <h3 className={`text-center text-base font-semibold ${
            theme 
              ? theme === 'custom' ? 'text-white' : themes[theme]?.text || 'text-white'
              : 'text-gray-900 dark:text-white'
          } mb-6`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
            What to expect
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            <div className="flex flex-col">
              <div className="flex items-center mb-3">
                <Clock className={`h-5 w-5 mr-2 ${
                  theme 
                    ? theme === 'custom' ? 'text-white' : themes[theme]?.text || 'text-white'
                    : 'text-primary-500 dark:text-primary-400'
                }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}} />
                <span className={`font-medium text-sm ${
                  theme 
                    ? theme === 'custom' ? 'text-white' : themes[theme]?.text || 'text-white'
                    : 'text-gray-900 dark:text-white'
                }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
                  Quick process
                </span>
              </div>
              <p className={`text-sm ${
                theme 
                  ? theme === 'custom' ? 'text-white/80' : themes[theme]?.subtext || 'text-white/80'
                  : 'text-gray-600 dark:text-gray-400'
              }`} style={theme === 'custom' && getSubtextStyle ? getSubtextStyle() : {}}>
                This will take less than 5 minutes to complete.
              </p>
            </div>
            
            <div className="flex flex-col">
              <div className="flex items-center mb-3">
                <Video className={`h-5 w-5 mr-2 ${
                  theme 
                    ? theme === 'custom' ? 'text-white' : themes[theme]?.text || 'text-white'
                    : 'text-primary-500 dark:text-primary-400'
                }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}} />
                <span className={`font-medium text-sm ${
                  theme 
                    ? theme === 'custom' ? 'text-white' : themes[theme]?.text || 'text-white'
                    : 'text-gray-900 dark:text-white'
                }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
                  Video response
                </span>
              </div>
              <p className={`text-sm ${
                theme 
                  ? theme === 'custom' ? 'text-white/80' : themes[theme]?.subtext || 'text-white/80'
                  : 'text-gray-600 dark:text-gray-400'
              }`} style={theme === 'custom' && getSubtextStyle ? getSubtextStyle() : {}}>
                We're looking for a short video response (2 minutes or less).
              </p>
            </div>
            
            <div className="flex flex-col">
              <div className="flex items-center mb-3">
                <CheckSquare className={`h-5 w-5 mr-2 ${
                  theme 
                    ? theme === 'custom' ? 'text-white' : themes[theme]?.text || 'text-white'
                    : 'text-primary-500 dark:text-primary-400'
                }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}} />
                <span className={`font-medium text-sm ${
                  theme 
                    ? theme === 'custom' ? 'text-white' : themes[theme]?.text || 'text-white'
                    : 'text-gray-900 dark:text-white'
                }`} style={theme === 'custom' && getTextStyle ? getTextStyle() : {}}>
                  Simple steps
                </span>
              </div>
              <p className={`text-sm ${
                theme 
                  ? theme === 'custom' ? 'text-white/80' : themes[theme]?.subtext || 'text-white/80'
                  : 'text-gray-600 dark:text-gray-400'
              }`} style={theme === 'custom' && getSubtextStyle ? getSubtextStyle() : {}}>
                Just share some basic info and record your response from any device.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}